import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  startOfWeek,
  endOfWeek,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  isToday,
} from 'date-fns';
import {
  DndContext,
  DragOverlay,
  type DragStartEvent,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { TopNav } from '@/components/layout/TopNav';
import { Composer } from '@/components/publishing/Composer';
import { postsApi, type Post } from '@/lib/api';
import { parseDbDate } from '@/lib/utils';
import { useAppStore } from '@/stores/appStore';

const STATUS_COLORS: Record<string, string> = {
  published: 'bg-success/70 text-success-foreground',
  scheduled: 'bg-info/70 text-info-foreground',
  draft: 'bg-muted text-muted-foreground',
  failed: 'bg-destructive/70 text-destructive-foreground',
};

function DraggablePost({ post }: { post: Post }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: post.id,
    data: { post },
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`rounded px-1.5 py-0.5 text-xs truncate cursor-grab active:cursor-grabbing select-none ${
        STATUS_COLORS[post.status] ?? 'bg-muted text-muted-foreground'
      } ${isDragging ? 'opacity-40' : ''}`}
      title={post.content}
    >
      {post.content.substring(0, 20)}
      {post.content.length > 20 ? '…' : ''}
    </div>
  );
}

function DroppableDay({
  day,
  children,
  isCurrentMonth,
  isToday: _isToday,
}: {
  day: Date;
  children: React.ReactNode;
  isCurrentMonth: boolean;
  isToday: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: day.toISOString() });

  return (
    <div
      ref={setNodeRef}
      className={`min-h-[100px] p-2 border-b border-r border-border transition-colors ${
        isOver ? 'bg-accent/5' : ''
      } ${!isCurrentMonth ? 'opacity-40' : ''}`}
    >
      <div
        className={`h-6 w-6 flex items-center justify-center rounded-full text-xs font-medium mb-1 ${
          _isToday ? 'bg-accent text-accent-foreground' : 'text-foreground'
        }`}
      >
        {format(day, 'd')}
      </div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

export function Calendar() {
  const { getToken } = useAuth();
  const { currentWorkspace } = useAppStore();
  const qc = useQueryClient();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [composerOpen, setComposerOpen] = useState(false);
  const [activeDragPost, setActiveDragPost] = useState<Post | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const calEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
  const days = eachDayOfInterval({ start: calStart, end: calEnd });

  const { data } = useQuery({
    queryKey: ['posts', currentWorkspace?.id, 'calendar', format(currentDate, 'yyyy-MM')],
    queryFn: async () => {
      const token = await getToken();
      // Fetch the whole visible grid, including the leading/trailing days of
      // the adjacent months, so those cells aren't falsely empty.
      return postsApi.list(token!, {
        workspaceId: currentWorkspace!.id,
        from: calStart.toISOString(),
        to: calEnd.toISOString(),
        limit: 200,
      });
    },
    enabled: !!currentWorkspace,
  });

  const reschedule = useMutation({
    mutationFn: async ({ postId, scheduledAt }: { postId: string; scheduledAt: string }) => {
      const token = await getToken();
      // Dragging a draft onto a date IS scheduling it — without the status
      // change the draft would sit on the calendar but never publish.
      return postsApi.update(token!, postId, { scheduled_at: scheduledAt, status: 'scheduled' });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['posts', currentWorkspace?.id] }),
  });

  const posts = data?.posts ?? [];

  const getPostsForDay = (day: Date): Post[] =>
    posts.filter((p) => {
      const dateStr = p.scheduled_at ?? p.published_at ?? p.created_at;
      return isSameDay(parseDbDate(dateStr), day);
    });

  function handleDragStart(event: DragStartEvent) {
    const post = posts.find((p) => p.id === event.active.id);
    if (post) setActiveDragPost(post);
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveDragPost(null);
    const { active, over } = event;
    if (!over) return;

    const postId = active.id as string;
    const targetDayIso = over.id as string;
    const targetDay = new Date(targetDayIso);

    const draggedPost = posts.find((p) => p.id === postId);
    if (!draggedPost) return;

    // Published/in-flight posts have already happened — rescheduling them
    // would re-publish the same record and destroy its history.
    if (!['draft', 'scheduled', 'failed'].includes(draggedPost.status)) return;

    // Preserve time from existing scheduled_at, or set to noon
    const existingDate = draggedPost.scheduled_at ? new Date(draggedPost.scheduled_at) : null;
    const newDate = new Date(targetDay);
    if (existingDate) {
      newDate.setHours(existingDate.getHours(), existingDate.getMinutes(), 0, 0);
    } else {
      newDate.setHours(12, 0, 0, 0);
    }

    // No time machine: a drop on a past day would publish on the next cron tick.
    if (newDate.getTime() < Date.now() && !isSameDay(newDate, new Date())) return;

    // Skip if same day
    if (existingDate && isSameDay(existingDate, newDate)) return;

    reschedule.mutate({ postId, scheduledAt: newDate.toISOString() });
  }

  return (
    <div className="flex-1 overflow-auto flex flex-col">
      <TopNav
        title="Calendar"
        action={{ label: 'New Post', onClick: () => setComposerOpen(true) }}
      />

      <div className="flex-1 overflow-auto p-6">
        {/* Month navigation */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setCurrentDate(subMonths(currentDate, 1))}
              className="p-2 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted/40"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <h2 className="text-lg font-semibold text-foreground min-w-[160px] text-center">
              {format(currentDate, 'MMMM yyyy')}
            </h2>
            <button
              onClick={() => setCurrentDate(addMonths(currentDate, 1))}
              className="p-2 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted/40"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <button
            onClick={() => setCurrentDate(new Date())}
            className="px-3 py-1.5 text-xs text-muted-foreground bg-tertiary ring-1 ring-border rounded-md hover:bg-tertiary/70"
          >
            Today
          </button>
        </div>

        {/* Calendar grid */}
        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            {/* Weekday headers */}
            <div className="grid grid-cols-7 border-b border-border">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div
                  key={day}
                  className="px-3 py-2 text-xs font-medium text-muted-foreground text-center"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Days */}
            <div className="grid grid-cols-7">
              {days.map((day) => {
                const dayPosts = getPostsForDay(day);
                return (
                  <DroppableDay
                    key={day.toISOString()}
                    day={day}
                    isCurrentMonth={isSameMonth(day, currentDate)}
                    isToday={isToday(day)}
                  >
                    {dayPosts.slice(0, 3).map((post) => (
                      <DraggablePost key={post.id} post={post} />
                    ))}
                    {dayPosts.length > 3 && (
                      <div className="text-xs text-muted-foreground px-1">
                        +{dayPosts.length - 3} more
                      </div>
                    )}
                  </DroppableDay>
                );
              })}
            </div>
          </div>

          <DragOverlay>
            {activeDragPost && (
              <div className={`rounded px-1.5 py-0.5 text-xs shadow-lg cursor-grabbing ${STATUS_COLORS[activeDragPost.status] ?? 'bg-muted text-muted-foreground'}`}>
                {activeDragPost.content.substring(0, 25)}
                {activeDragPost.content.length > 25 ? '…' : ''}
              </div>
            )}
          </DragOverlay>
        </DndContext>
      </div>

      {composerOpen && <Composer onClose={() => setComposerOpen(false)} />}
    </div>
  );
}
